import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { Gift, Heart, Clock, Euro, User, MessageSquare } from 'lucide-react';
import Button from '../components/ui/Button';
import { useServiceCategories } from '../hooks/useServiceCategories';
import { useServicesByCategory } from '../hooks/useServices';
import { getService } from '../services/api';
import { giftVoucherService } from '../services/giftVoucherService';
import PaymentSection from '../components/payments/PaymentSection';
import { paymentService } from '../services/paymentService';
import { promoCodeService } from '../services/promoCodeService';
import { stripePromise } from '../lib/stripe';
import { scrollAndFocus } from '../utils/scroll';
import { usePaymentCleanup } from '../hooks/usePaymentCleanup';
import SEOHead from '../components/SEOHead';

const giftVoucherSchema = z.object({
  service_id: z.string().min(1, "Veuillez choisir un service"),
  purchaser_firstname: z.string().min(2, "Le prénom doit contenir au moins 2 caractères"),
  purchaser_name: z.string().min(2, "Le nom doit contenir au moins 2 caractères"),
  purchaser_email: z.string().email("Adresse email invalide"),
  purchaser_phone: z.string().regex(/^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/, "Numéro de téléphone invalide").optional().or(z.literal("")),
  purchaser_address_line1: z.string().min(1, "L'adresse est obligatoire"),
  purchaser_address_line2: z.string().optional(),
  purchaser_postal_code: z.string().regex(/^\d{5}$/, "Code postal invalide (5 chiffres)"),
  purchaser_city: z.string().min(1, "La ville est obligatoire"),
  purchaser_country: z.literal("France"),
  recipient_firstname: z.string().min(2, "Le prénom du bénéficiaire doit contenir au moins 2 caractères"),
  recipient_lastname: z.string().min(2, "Le nom du bénéficiaire doit contenir au moins 2 caractères"),
  personal_message: z.string().max(200, "Le message ne peut pas dépasser 200 caractères").refine(
    (val) => !val || !/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2702}-\u{27B0}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2300}-\u{23FF}]/u.test(val),
    { message: "Les emojis ne sont pas autorisés dans le message" }
  ).optional(),
  validity_months: z.number().default(9),
});

type GiftVoucherForm = z.infer<typeof giftVoucherSchema>;

const GiftVouchers: React.FC = () => {
  // Synchronously check for post-payment redirect to prevent UI flash
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const isPostPaymentRedirect = params.has('payment_intent_client_secret') && params.has('voucher_id');
  const [selectedCategorySlug, setSelectedCategorySlug] = useState<string | null>(null);
  const [showServices, setShowServices] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdVoucher, setCreatedVoucher] = useState<any>(null);
  const [paymentClientSecret, setPaymentClientSecret] = useState<string | null>(null);
  const [isConfirmingPayment, setIsConfirmingPayment] = useState<boolean>(isPostPaymentRedirect);
  const [paymentError, setPaymentError] = useState<string | null>(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    if (params.has('payment_failed')) {
      return 'Le paiement a échoué. Veuillez réessayer ou utiliser un autre moyen de paiement.';
    }
    return null;
  });
  const [ariaMessage, setAriaMessage] = useState('');
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState<number | null>(null);
  const [promoValidCode, setPromoValidCode] = useState<string | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [effectiveAmountCents, setEffectiveAmountCents] = useState<number | null>(null);
  const [preselectedServiceId, setPreselectedServiceId] = useState<string | null>(null);

  // Fetch categories and services from API
  const { categories, loading: categoriesLoading } = useServiceCategories();
  const { services, loading: servicesLoading } = useServicesByCategory(selectedCategorySlug || '');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm<GiftVoucherForm>({
    resolver: zodResolver(giftVoucherSchema),
    defaultValues: {
      validity_months: 9
    }
  });

  const selectedServiceId = watch('service_id');
  const selectedService = services.find(s => s.id.toString() === selectedServiceId);

  // Pré-sélection via ?service=<id> dans l'URL
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const serviceParam = params.get('service');
    if (!serviceParam) return;

    const serviceId = Number(serviceParam);
    if (!Number.isFinite(serviceId) || serviceId <= 0) return;

    let cancelled = false;
    (async () => {
      try {
        const service = await getService(serviceId);
        if (cancelled || !service.is_active) return;
        setSelectedCategorySlug(service.category.slug);
        setShowServices(true);
        setPreselectedServiceId(String(service.id));
      } catch (err) {
        console.warn('Service préselectionné introuvable :', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Applique la pré-sélection une fois que la liste des services est chargée
  useEffect(() => {
    if (!preselectedServiceId) return;
    if (servicesLoading) return;
    const exists = services.some(s => String(s.id) === preselectedServiceId);
    if (exists) {
      setValue('service_id', preselectedServiceId);
    }
  }, [preselectedServiceId, services, servicesLoading, setValue]);

  // Extraire le PaymentIntent ID du clientSecret (format: pi_xxx_secret_yyy)
  const paymentIntentId = paymentClientSecret ? paymentClientSecret.split('_secret_')[0] : null;
  
  // Hook pour annuler automatiquement le PaymentIntent si l'utilisateur quitte la page
  usePaymentCleanup({
    paymentIntentId,
    isPaymentComplete: createdVoucher?.payment_status === 'paid' || isConfirmingPayment,
    enabled: !!paymentClientSecret && createdVoucher?.payment_status !== 'paid',
  });

  // When a service is selected, scroll to buyer info
  useEffect(() => {
    if (!selectedServiceId) return;
    const tryScroll = () => {
      const buyer = document.querySelector('[data-section="buyer"]') as HTMLElement | null;
      if (buyer) {
        scrollAndFocus(buyer, { hash: '#acheteur', announce: setAriaMessage, message: 'Étape 2 : Vos informations', focusSelector: 'h3, input', pushState: true });
      } else {
        requestAnimationFrame(tryScroll);
      }
    };
    setTimeout(() => requestAnimationFrame(tryScroll), 100);
  }, [selectedServiceId]);

  const handleCategorySelect = (categorySlug: string) => {
    setSelectedCategorySlug(categorySlug);
    setShowServices(true);
    // Scroll to services section
    setTimeout(() => {
      const el = document.querySelector('[data-section="services"]') as HTMLElement | null;
      if (el) {
        scrollAndFocus(el, { hash: '#services', announce: setAriaMessage, message: 'Étape 1 : Sélectionnez le service', focusSelector: 'h3', pushState: true });
      }
    }, 100);
  };

  useEffect(() => {
    if (createdVoucher) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [createdVoucher]);

  // Post-payment redirect handling
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const clientSecret = params.get('payment_intent_client_secret');
    const voucherIdStr = params.get('voucher_id');

    if (clientSecret && voucherIdStr) {
      setIsConfirmingPayment(true);
      (async () => {
        try {
          const stripe = await stripePromise;
          if (!stripe) {
            setIsConfirmingPayment(false);
            return;
          }

          const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
          if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) {
            const voucherId = parseInt(voucherIdStr, 10);

            // Mark the voucher as paid
            await giftVoucherService.markPaid(voucherId, paymentIntent.id);

            // Fetch the full voucher details to display
            const fetchedVoucher = await giftVoucherService.getById(voucherId);

            const serviceName =
              services.find(s => s.id === Number((fetchedVoucher as any).service_id))?.name ||
              (fetchedVoucher as any)?.service?.name ||
              createdVoucher?.service_name ||
              null;

            // Ensure the voucher is marked as paid in the state
            // Récupérer le montant réellement payé depuis le PaymentIntent Stripe
            if (paymentIntent.amount) {
              setEffectiveAmountCents(paymentIntent.amount);
            }

            const voucherWithCorrectStatus = {
              ...fetchedVoucher,
              payment_status: 'paid', // Force the status to be 'paid' since we just confirmed it
              stripe_payment_intent_id: paymentIntent.id,
              service_name: serviceName,
            };
            setCreatedVoucher(voucherWithCorrectStatus);

            // Clean up URL
            window.history.replaceState(null, '', window.location.pathname);
          }
        } catch (e) {
          console.error('Erreur lors de la vérification du paiement du bon cadeau:', e);
          setPaymentError('Une erreur est survenue lors de la vérification du paiement.');
        }
        finally {
          setIsConfirmingPayment(false);
        }
      })();
    }
  }, [services]);

  // Fallback: if we land back with only voucher_id (after PaymentStatus redirect), just fetch the voucher and show confirmation
  
  // After hooks are declared: if we're currently confirming payment, show a loading screen
  if (isConfirmingPayment) {
    return (
      <div className="pt-24 pb-16 bg-neutral-offwhite min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-playfair text-3xl font-bold text-neutral-ink mb-4">Validation du paiement…</h2>
          <p className="font-nunito text-neutral-body">Nous finalisons l’activation de votre bon cadeau.</p>
        </div>
      </div>
    );
  }

  const onSubmit = async (data: GiftVoucherForm) => {
    if (!selectedService) return;

    setIsSubmitting(true);
    try {
      const voucherData = {
        amount_cents: selectedService.price || 0,
        service_id: parseInt(data.service_id),
        purchaser_firstname: data.purchaser_firstname,
        purchaser_name: data.purchaser_name,
        purchaser_email: data.purchaser_email,
        purchaser_phone: data.purchaser_phone || undefined,
        purchaser_address_line1: data.purchaser_address_line1,
        purchaser_address_line2: data.purchaser_address_line2 || undefined,
        purchaser_postal_code: data.purchaser_postal_code,
        purchaser_city: data.purchaser_city,
        purchaser_country: data.purchaser_country,
        recipient_firstname: data.recipient_firstname || undefined,
        recipient_lastname: data.recipient_lastname || undefined,
        personal_message: data.personal_message || undefined,
        validity_months: data.validity_months,
      };

      const voucher = await giftVoucherService.create(voucherData);
      const voucherWithMeta = {
        ...voucher,
        service_name: selectedService.name,
      };

      // Immediately create the payment intent for the newly created voucher
      try {
        const resp = await paymentService.createPaymentIntent({
          type: 'voucher',
          voucher_id: voucher.id,
          amount_cents: voucher.amount_cents,
          customer_email: voucher.purchaser_email,
          ...(promoValidCode ? { promo_code: promoValidCode } : {}),
        });
        const discounted = (promoDiscount && voucher.amount_cents)
          ? Math.max(50, Math.round(voucher.amount_cents * (100 - promoDiscount) / 100))
          : voucher.amount_cents;
        setEffectiveAmountCents(discounted);
        setPaymentClientSecret(resp.client_secret);
        setCreatedVoucher(voucherWithMeta); // Affiche la section paiement
      } catch (e: any) {
        console.error("Erreur lors de la création de l'intention de paiement:", e);
        setPaymentError(e?.message || "Une erreur critique est survenue lors de la préparation du paiement. Veuillez réessayer.");
        // We still set the voucher to show the error message on the next screen
        setCreatedVoucher(voucherWithMeta);
      }
    } catch (error) {
      console.error('Erreur lors de la création du bon cadeau:', error);
      alert('Erreur lors de la création du bon cadeau');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (createdVoucher) {
    return (
      <div className="pt-24 pb-16 bg-neutral-offwhite">
        <div className="container mx-auto px-4">
        <div aria-live="polite" role="status" className="sr-only">{ariaMessage}</div>
          <div className="max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-lg p-8 md:p-12"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
                <Gift className="text-green-600" size={32} />
              </div>
              
              {isConfirmingPayment ? (
                <>
                  <h2 className="font-playfair text-3xl font-bold text-neutral-ink mb-4">Validation du paiement…</h2>
                  <p className="font-nunito text-neutral-body mb-8">Nous finalisons l’activation de votre bon cadeau.</p>
                </>
              ) : createdVoucher.payment_status === 'paid' ? (
                <>
                  <h2 className="font-playfair text-3xl font-bold text-neutral-ink mb-4">
                    Merci pour votre achat !
                  </h2>
                  <p className="font-nunito text-neutral-body mb-4">
                    Nous avons bien reçu votre paiement. Le bon cadeau sera envoyé automatiquement à l’adresse email indiquée dès la validation par notre système.
                  </p>
                  <div className="bg-neutral-offwhite border border-neutral-line rounded-2xl p-6 text-left mb-8">
                    <h3 className="font-playfair text-xl font-semibold text-neutral-ink mb-4">Récapitulatif de votre commande</h3>
                    <ul className="space-y-2 font-nunito text-neutral-body">
                      <li><span className="font-semibold text-neutral-ink">Service :</span> {createdVoucher.service_name || createdVoucher.service?.name || selectedService?.name || 'Prestation bien-être'}</li>
                      <li><span className="font-semibold text-neutral-ink">Montant :</span> {((effectiveAmountCents ?? createdVoucher.amount_paid_cents ?? createdVoucher.amount_cents) / 100).toFixed(2)} €</li>
                      <li><span className="font-semibold text-neutral-ink">Destinataire :</span> {(createdVoucher.recipient_firstname && createdVoucher.recipient_lastname) ? `${createdVoucher.recipient_firstname} ${createdVoucher.recipient_lastname}` : `${createdVoucher.purchaser_firstname} ${createdVoucher.purchaser_name}`}</li>
                      <li><span className="font-semibold text-neutral-ink">Achat confirmé :</span> {new Date().toLocaleDateString('fr-FR')}</li>
                    </ul>
                  </div>
                  <p className="font-nunito text-neutral-body">
                    Vous recevrez un courriel contenant le bon cadeau (format PDF) dans les prochaines minutes. Pensez à vérifier votre dossier spam si besoin.
                  </p>
                </>
              ) : (
                <>
                  <h2 className="font-playfair text-3xl font-bold text-neutral-ink mb-4">
                    Finalisez votre achat
                  </h2>
                  <p className="font-nunito text-neutral-body mb-8">
                    Veuillez procéder au paiement pour activer votre bon cadeau.
                  </p>
                </>
              )}
              
              {createdVoucher.payment_status !== 'paid' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6 text-left">
                  <p className="text-yellow-800 text-sm">
                    <strong>Important :</strong> Ce bon cadeau doit être payé pour être activé. Une fois le paiement validé, vous pourrez le télécharger.
                  </p>
                  <p className="text-yellow-800 text-xs mt-1">
                    Montant: {((effectiveAmountCents ?? createdVoucher.amount_paid_cents ?? createdVoucher.amount_cents) / 100).toFixed(2)}€
                  </p>
                </div>
              )}

              {paymentError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm mb-4">
                  {paymentError}
                </div>
              )}

              {paymentClientSecret && (
                <div className="text-left mb-8">
                  <h3 className="font-playfair font-bold text-xl text-neutral-ink mb-4">
                    Paiement sécurisé
                  </h3>
                  <PaymentSection
                    clientSecret={paymentClientSecret}
                    amountCents={effectiveAmountCents ?? createdVoucher.amount_paid_cents ?? createdVoucher.amount_cents}
                    returnUrl={typeof window !== 'undefined' ? `${window.location.origin}/bons-cadeaux?voucher_id=${createdVoucher.id}` : undefined}
                    onSuccess={async (pi) => {
                      try {
                        await giftVoucherService.markPaid(createdVoucher.id, pi);
                        setCreatedVoucher({
                          ...createdVoucher,
                          payment_status: 'paid',
                          stripe_payment_intent_id: pi,
                          service_name: createdVoucher.service_name || selectedService?.name || createdVoucher.service?.name || null,
                        });
                      } catch (err) {
                        // eslint-disable-next-line no-console
                        console.error('Erreur de confirmation paiement bon cadeau:', err);
                      }
                    }}
                    onError={(m) => setPaymentError(m)}
                  />
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="primary"
                  onClick={() => {
                    setCreatedVoucher(null);
                    setSelectedCategorySlug(null);
                    setShowServices(false);
                    setPaymentClientSecret(null);
                    setPaymentError(null);
                  }}
                >
                  Créer un autre bon
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16 bg-neutral-offwhite">
      <SEOHead
        title="Bons Cadeaux Spa & Massage"
        description="Offrez un bon cadeau spa bébé ou massage prénatal. Livraison par email immédiate — Doux ce Moman, Geneuille (25)."
        path="/bons-cadeaux"
      />
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-neutral-ink mb-6">
            Bons Cadeaux
          </h1>
          <p className="font-nunito text-neutral-body max-w-2xl mx-auto text-lg">
            Offrez un moment de détente et de bien-être avec nos bons cadeaux personnalisés.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          
          {/* Section Vérification Bon Cadeau */}
          <div className="bg-gradient-to-r from-accent-rose/20 to-accent-sand/20 rounded-2xl p-6 border border-brand/30 mb-8">
            <div className="flex items-center justify-center mb-4">
              <Gift className="text-brand mr-3" size={24} />
              <h3 className="font-playfair font-bold text-xl text-neutral-ink">
                Vous avez déjà un bon cadeau ?
              </h3>
            </div>
            <p className="text-center text-neutral-body mb-6 font-nunito">
              Utilisez votre bon cadeau existant pour réserver directement votre séance
            </p>
            
            <div className="text-center">
              <Button
                variant="secondary"
                to="/reservation?expand_gift_voucher=true"
                className="inline-flex items-center"
              >
                <Gift className="mr-2" size={16} />
                Utiliser mon bon cadeau
              </Button>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            
            {/* Sélection du service */}
            <div className="space-y-6">
              <h2 className="font-playfair font-bold text-2xl text-center text-neutral-ink">
                Choisissez le service à offrir
              </h2>
              
              {/* Sélection de la catégorie */}
              <div className="space-y-4">
                <label className="block font-playfair font-bold text-neutral-ink mb-6 text-center text-xl">
                  Catégorie de soin
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {categoriesLoading ? (
                    <div className="col-span-full text-center py-8">
                      <p className="text-neutral-body">Chargement des catégories...</p>
                    </div>
                  ) : categories.length === 0 ? (
                    <div className="col-span-full text-center py-8">
                      <p className="text-neutral-body">Aucune catégorie disponible pour le moment.</p>
                    </div>
                  ) : (
                    categories.map((category) => (
                      <motion.div
                        key={category.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <button
                          type="button"
                          onClick={() => handleCategorySelect(category.slug)}
                          className={`w-full h-32 relative overflow-hidden rounded-xl transition-all duration-200 group border-2
                            ${selectedCategorySlug === category.slug 
                              ? 'bg-brand text-neutral-offwhite ring-2 ring-brand shadow-lg border-brand' 
                              : 'bg-white border-neutral-line hover:border-brand hover:bg-brand/10'
                            }`}
                        >
                          <div className="absolute inset-0 flex flex-col justify-center items-center p-4">
                            <div className="flex items-center mb-2">
                              <Heart className={`mr-2 ${selectedCategorySlug === category.slug ? 'text-neutral-offwhite' : 'text-brand'}`} size={24} />
                              <h3 className={`font-playfair font-bold text-lg ${selectedCategorySlug === category.slug ? 'text-neutral-offwhite' : 'text-neutral-ink'}`}>{category.name}</h3>
                            </div>
                            <p className={`font-nunito text-sm text-center whitespace-pre-line ${selectedCategorySlug === category.slug ? 'text-neutral-offwhite/90' : 'text-neutral-body'}`}>{category.description}</p>
                          </div>
                        </button>
                      </motion.div>
                    ))
                  )}
                </div>
              </div>

              {/* Liste des services */}
              {showServices && selectedCategorySlug && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-section="services"
                  className="space-y-4"
                >
                  <h3 className="font-playfair font-bold text-xl text-center text-neutral-ink mb-6">
                    Sélectionnez le service
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {servicesLoading ? (
                      <div className="col-span-full text-center py-8">
                        <p className="text-neutral-body">Chargement des services...</p>
                      </div>
                    ) : services.length === 0 ? (
                      <div className="col-span-full text-center py-8">
                        <p className="text-neutral-body">Aucun service disponible dans cette catégorie.</p>
                      </div>
                    ) : (
                      services.map((service) => (
                        <motion.label
                          key={service.id}
                          className={`
                            relative border-2 rounded-xl p-6 cursor-pointer transition-all duration-200
                            ${selectedServiceId === service.id.toString()
                              ? 'bg-brand text-neutral-offwhite ring-2 ring-brand shadow-lg border-brand'
                              : 'bg-white border-neutral-line hover:border-brand hover:bg-brand/10'
                            }
                          `}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <input
                            type="radio"
                            value={service.id.toString()}
                            {...register('service_id')}
                            className="sr-only"
                          />
                          <div className="flex flex-col h-full">
                            <span className={`font-playfair font-bold text-lg mb-2 ${selectedServiceId === service.id.toString() ? 'text-neutral-offwhite' : 'text-neutral-ink'}`}>{service.name}</span>
                            <div className={`flex items-center space-x-4 text-sm mb-2 ${selectedServiceId === service.id.toString() ? 'text-neutral-offwhite/90' : 'text-neutral-body'}`}>
                              <div className="flex items-center">
                                <Clock size={16} className="mr-1" />
                                <span>{service.duration_minutes} min</span>
                              </div>
                              <div className="flex items-center">
                                <Euro size={16} className="mr-1" />
                                <span>{service.price ? (service.price / 100).toFixed(0) : '0'}€</span>
                              </div>
                            </div>
                            <p className={`text-sm whitespace-pre-line ${selectedServiceId === service.id.toString() ? 'text-neutral-offwhite/80' : 'text-neutral-body'}`}>{service.description}</p>
                          </div>
                        </motion.label>
                      ))
                    )}
                  </div>
                  {errors.service_id && (
                    <p className="text-red-500 text-sm text-center">{errors.service_id.message}</p>
                  )}
                </motion.div>
              )}
            </div>

            {selectedService && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-8"
              >
                {/* Informations acheteur */}
                <div data-section="buyer" className="bg-neutral-offwhite rounded-xl p-6">
                  <h3 className="font-playfair font-bold text-xl text-neutral-ink mb-6 flex items-center">
                    <User className="mr-2" size={20} />
                    Vos informations
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-nunito font-semibold text-neutral-body mb-2">
                        Prénom
                      </label>
                      <input
                        type="text"
                        {...register('purchaser_firstname')}
                        className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                        placeholder="Votre prénom"
                        autoComplete="given-name"
                      />
                      {errors.purchaser_firstname && (
                        <p className="text-red-500 text-sm mt-1">{errors.purchaser_firstname.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block font-nunito font-semibold text-neutral-body mb-2">
                        Nom
                      </label>
                      <input
                        type="text"
                        {...register('purchaser_name')}
                        className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                        placeholder="Votre nom"
                      />
                      {errors.purchaser_name && (
                        <p className="text-red-500 text-sm mt-1">{errors.purchaser_name.message}</p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <label className="block font-nunito font-semibold text-neutral-body mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        {...register('purchaser_email')}
                        className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                        placeholder="votre@email.com"
                      />
                      {errors.purchaser_email && (
                        <p className="text-red-500 text-sm mt-1">{errors.purchaser_email.message}</p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <label className="block font-nunito font-semibold text-neutral-body mb-2">
                        Téléphone (optionnel)
                      </label>
                      <input
                        type="tel"
                        {...register('purchaser_phone')}
                        className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                        placeholder="06 12 34 56 78"
                      />
                      {errors.purchaser_phone && (
                        <p className="text-red-500 text-sm mt-1">{errors.purchaser_phone.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Section Adresse */}
                  <div className="mt-8 pt-8 border-t border-neutral-line">
                    <h4 className="font-playfair text-lg font-semibold text-neutral-ink mb-4">
                      Votre adresse
                    </h4>
                    <div className="grid grid-cols-1 gap-6">
                      <div>
                        <label className="block font-nunito font-semibold text-neutral-body mb-2">
                          Adresse
                        </label>
                        <input
                          type="text"
                          {...register('purchaser_address_line1')}
                          className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                          placeholder="Numéro et nom de rue"
                        />
                        {errors.purchaser_address_line1 && (
                          <p className="text-red-500 text-sm mt-1">{errors.purchaser_address_line1.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block font-nunito font-semibold text-neutral-body mb-2">
                          Complément d'adresse (optionnel)
                        </label>
                        <input
                          type="text"
                          {...register('purchaser_address_line2')}
                          className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                          placeholder="Appartement, bâtiment, etc. (optionnel)"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block font-nunito font-semibold text-neutral-body mb-2">
                            Code postal
                          </label>
                          <input
                            type="text"
                            {...register('purchaser_postal_code')}
                            maxLength={5}
                            className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                            placeholder="75001"
                          />
                          {errors.purchaser_postal_code && (
                            <p className="text-red-500 text-sm mt-1">{errors.purchaser_postal_code.message}</p>
                          )}
                        </div>

                        <div>
                          <label className="block font-nunito font-semibold text-neutral-body mb-2">
                            Ville
                          </label>
                          <input
                            type="text"
                            {...register('purchaser_city')}
                            className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                            placeholder="Paris"
                          />
                          {errors.purchaser_city && (
                            <p className="text-red-500 text-sm mt-1">{errors.purchaser_city.message}</p>
                          )}
                        </div>
                      </div>

                      <input type="hidden" {...register('purchaser_country')} value="France" />
                    </div>
                  </div>
                </div>

                {/* Informations bénéficiaire */}
                <div className="bg-brand/10 rounded-xl p-6">
                  <h3 className="font-playfair font-bold text-xl text-neutral-ink mb-6 flex items-center">
                    <Gift className="mr-2" size={20} />
                    Bénéficiaire du bon cadeau
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-nunito font-semibold text-neutral-body mb-2">
                        Prénom du bénéficiaire
                      </label>
                      <input
                        type="text"
                        {...register('recipient_firstname')}
                        className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                        placeholder="Prénom du bénéficiaire"
                      />
                      {errors.recipient_firstname && (
                        <p className="text-red-500 text-sm mt-1">{errors.recipient_firstname.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block font-nunito font-semibold text-neutral-body mb-2">
                        Nom du bénéficiaire
                      </label>
                      <input
                        type="text"
                        {...register('recipient_lastname')}
                        className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                        placeholder="Nom du bénéficiaire"
                      />
                      {errors.recipient_lastname && (
                        <p className="text-red-500 text-sm mt-1">{errors.recipient_lastname.message}</p>
                      )}
                    </div>


                    <div className="md:col-span-2">
                      <label className="block font-nunito font-semibold text-neutral-body mb-2 flex items-center">
                        <MessageSquare className="mr-2" size={16} />
                        Message personnel (optionnel)
                      </label>
                      <textarea
                        {...register('personal_message')}
                        maxLength={200}
                        className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                        rows={4}
                        placeholder="Ajoutez un message personnel pour accompagner votre bon cadeau..."
                      />
                      <div className="flex justify-between items-center mt-1">
                        <div>
                          {errors.personal_message && (
                            <p className="text-red-500 text-sm">{errors.personal_message.message}</p>
                          )}
                        </div>
                        <p className="text-neutral-body text-sm">
                          {watch('personal_message')?.length || 0}/200
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Récapitulatif */}
                <div className="bg-gradient-to-r from-accent-rose/20 to-accent-sand/20 rounded-xl p-6 border border-brand/30">
                  <h3 className="font-playfair font-bold text-xl text-neutral-ink mb-4">
                    Récapitulatif
                  </h3>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="font-nunito">Service :</span>
                      <span className="font-semibold">{selectedService.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-nunito">Durée :</span>
                      <span>{selectedService.duration_minutes} minutes</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-nunito">Validité :</span>
                      <span>9 mois</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold">
                      <span>Prix :</span>
                      <span>
                        {selectedService.price ? (
                          promoDiscount ? (
                            <>
                              <span className="line-through text-neutral-body font-normal mr-2">{(selectedService.price / 100).toFixed(0)}€</span>
                              <span className="text-green-600">{(Math.max(50, Math.round(selectedService.price * (100 - promoDiscount) / 100)) / 100).toFixed(0)}€</span>
                            </>
                          ) : `${(selectedService.price / 100).toFixed(0)}€`
                        ) : '0€'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Code promo */}
                {selectedService?.price && (
                  <div className="bg-neutral-offwhite rounded-xl p-4 border border-neutral-line">
                    <label className="block font-playfair font-bold text-neutral-ink mb-2">
                      Code promotionnel (optionnel)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => {
                          setPromoCode(e.target.value.toUpperCase());
                          if (promoValidCode) {
                            setPromoValidCode(null);
                            setPromoDiscount(null);
                            setPromoError(null);
                          }
                        }}
                        disabled={!!promoValidCode}
                        className="flex-1 p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none uppercase tracking-wider disabled:bg-gray-100"
                        placeholder="Ex: BIENVENUE20"
                      />
                      {promoValidCode ? (
                        <button
                          type="button"
                          onClick={() => {
                            setPromoCode('');
                            setPromoValidCode(null);
                            setPromoDiscount(null);
                            setPromoError(null);
                          }}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl font-nunito hover:bg-gray-300 transition-colors"
                        >
                          Retirer
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={!promoCode.trim() || promoLoading}
                          onClick={async () => {
                            setPromoLoading(true);
                            setPromoError(null);
                            try {
                              const result = await promoCodeService.validate(promoCode);
                              if (result.valid && result.discount_percent) {
                                setPromoDiscount(result.discount_percent);
                                setPromoValidCode(result.code || promoCode);
                              } else {
                                setPromoError(result.error || 'Code invalide');
                              }
                            } catch (e: any) {
                              setPromoError(e.message || 'Erreur de validation');
                            } finally {
                              setPromoLoading(false);
                            }
                          }}
                          className="px-4 py-2 bg-brand text-white rounded-xl font-nunito hover:bg-brand/90 transition-colors disabled:opacity-50"
                        >
                          {promoLoading ? '...' : 'Appliquer'}
                        </button>
                      )}
                    </div>
                    {promoError && (
                      <p className="text-red-500 text-sm mt-2">{promoError}</p>
                    )}
                    {promoValidCode && promoDiscount && (
                      <p className="text-green-600 text-sm mt-2 font-medium">
                        Code {promoValidCode} appliqué : -{promoDiscount}% de réduction
                      </p>
                    )}
                  </div>
                )}

                {/* Bouton de soumission */}
                <div className="text-center">
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full md:w-auto"
                  >
                    {isSubmitting ? 'Création en cours...' : 'Créer le bon cadeau'}
                  </Button>
                </div>
              </motion.div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default GiftVouchers;
